// src/pages/customers/CustomersPage.js

import React, { useMemo, useContext } from 'react';
import GenericListPage from '../GenericListPage';
import useApi from '../../hooks/useApi';
import { AuthContext } from '../../contexts/AuthContext';

// --- small CSV helpers ---
function escapeCsv(val) {
  if (val == null) return '';
  return String(val).replace(/"/g, '""');
}
function downloadCsvString(csvString, filename = 'report.csv') {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 500);
}

const CustomersPage = () => {
  const api = useApi();
  const { settings } = useContext(AuthContext);

  const customerColumns = useMemo(() => [
    { header: 'Customer ID', key: '_id' },
    { header: 'Name', key: 'name' },
    { header: 'Email', key: 'email' },
    { header: 'Phone', key: 'phone' },
    { header: 'Created At', key: 'createdAt', render: (c) => new Date(c.createdAt).toLocaleString() },
    // add more columns as needed
  ], [settings]);

  const customerFilterConfig = useMemo(() => [
    { name: 'search', type: 'text', label: 'Search (name, email, phone)' },
    { name: 'createdFrom', type: 'date', label: 'Created From' },
    { name: 'createdTo', type: 'date', label: 'Created To' },
  ], []);

  // Export handler: call existing API and request no pagination
  const handleDownloadCsv = async (currentFilters = {}) => {
    try {
      const queryParams = { ...currentFilters, noPagination: true, export: 'csv' };
      // use same api.get signature as other pages; adapt if your hook needs different args
      const resp = await api.get('customers', { queryParams });
      const rows = Array.isArray(resp) ? resp : (resp?.data ?? []);

      if (!rows || rows.length === 0) {
        alert('No customers found for the selected filters.');
        return;
      }

      // Define CSV columns mapping (key supports nested like 'address.city')
      const cols = [
        { header: 'ID', key: '_id' },
        { header: 'Name', key: 'name' },
        { header: 'Email', key: 'email' },
        { header: 'Phone', key: 'phone' },
        { header: 'Total Purchases', key: 'stats.totalOrders' }, // example nested field
        { header: 'Total Amount Spent', key: 'stats.totalAmountSpent' }, // example nested field
        { header: 'Created At', key: 'createdAt', transform: (r) => r.createdAt ? new Date(r.createdAt).toISOString() : '' },
      ];

      // Build CSV
      const header = cols.map(c => `"${escapeCsv(c.header)}"`).join(',');
      const body = rows.map(row => cols.map(col => {
        let v = '';
        if (typeof col.transform === 'function') v = col.transform(row);
        else if (col.key) {
          v = col.key.split('.').reduce((o, k) => (o ? o[k] : ''), row);
        }
        return `"${escapeCsv(v ?? '')}"`;
      }).join(',')).join('\r\n');
      const csvString = `${header}\r\n${body}`;

      const filename = `customers-${new Date().toISOString().slice(0,10)}.csv`;
      downloadCsvString(csvString, filename);
    } catch (err) {
      console.error('Customer CSV export failed', err);
      alert('Unable to export customers CSV. Check console for details.');
    }
  };

  return (
    <GenericListPage
      title="Customers"
      permission="manage_customers"
      entityEndpoint="customers"
      columns={customerColumns}
      FormComponent={null}
      filterConfig={customerFilterConfig}
      onDownloadCsv={handleDownloadCsv}
    />
  );
};

export default CustomersPage;
